import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import {
    Box,
    CircularProgress,
    FormHelperText,
    InputAdornment,
    Radio,
    TextField,
    Typography,
} from "@mui/material";
import IconButton from "@mui/joy/IconButton/IconButton";
import {
    ChevronLeftRounded,
    ChevronRightRounded,
    CloseRounded,
    Graph2Rounded,
    SearchRounded,
} from "@mui-symbols-material/w300";
import { useTranslation } from "react-i18next";
import { BoxIcon, getInstalledBaseTypeIcon } from "@dreebit-registry/vsm-microservice-components";
import { InfrastructureSelect as InfrastructureSelectQuery } from "../../graphql/queries/infrastructureSelect";
import { useInfrastructureNodeList, InfrastructureNode } from "../../hooks/useInfrastructureNodeList";
import { revealRowInScroller } from "../../utils/columnScroll";

type PartialInstalledBaseNode = InfrastructureNode;

const SELECTED_BG = "#E5E5E5";
const LIST_SCROLL_THRESHOLD = 48;

type Props = {
    onChange?: (id: string | null) => void;
    value?: string;
    label?: string;
    error?: boolean;
    helperText?: string;
    /** Restricts browsing to the subtree below this node; the company list is never shown. */
    rootNodeId?: string;
    /**
     * Flat company (client root) list only: no drill-down, row click toggles
     * the selection. Not meant to be combined with `rootNodeId`.
     */
    selectRootOnly?: boolean;
    /**
     * Only with `rootNodeId`: the scoped root itself is offered for selection
     * via a radio in the top-level header.
     */
    rootSelectable?: boolean;
    /** Pass `false` in destination pickers to hide the TRASH node and everything
     *  inside it (elements only reach the trash by being deleted). Omitted, the
     *  server default applies and the trash stays browsable/selectable. */
    includeTrash?: boolean;
};

const LIST_HEIGHT = 240;

const InfrastructureBrowser = ({ onChange, label, value, error, helperText, rootNodeId, selectRootOnly, rootSelectable, includeTrash }: Props) => {
    const { t } = useTranslation();

    const normalizedValue = value != null && value !== "" ? String(value) : null;
    const scopedRootId = rootNodeId != null && rootNodeId !== "" ? String(rootNodeId) : null;

    const [parentNodeId, setParentNodeId] = useState<string | null>(scopedRootId);
    const [parentNodeHistory, setParentNodeHistory] = useState<{ id: string | null }[]>([]);

    const isRoot = parentNodeId === null;
    const atTopLevel = scopedRootId !== null ? parentNodeId === scopedRootId : isRoot;

    const { data: selectedNodeData, loading: selectedNodeLoading } = useQuery(
        InfrastructureSelectQuery,
        {
            variables: { id: normalizedValue },
            skip: !normalizedValue,
            fetchPolicy: "cache-first",
        }
    );

    const selectedNode: PartialInstalledBaseNode | null =
        selectedNodeData?.installedBaseNode ?? null;

    // Open into the selected node's parent context on mount/when selection changes
    useEffect(() => {
        // Root-only mode never leaves the company list, no matter what is selected.
        if (selectRootOnly) return;
        if (selectedNode) {
            const path = selectedNode.pathNodes ?? [];
            const parentOfSelected = path.length >= 2 ? path[path.length - 2].id : null;
            setParentNodeId(parentOfSelected ?? scopedRootId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedNode?.id]);

    const { data: parentNodeData, loading: parentNodeLoading } = useQuery(
        InfrastructureSelectQuery,
        {
            variables: { id: parentNodeId },
            skip: !parentNodeId,
            fetchPolicy: "cache-first",
        }
    );

    const {
        searchInput,
        onSearchInputChange,
        onClearSearch,
        searchQuery,
        rows: filteredEntries,
        loading: listLoading,
        loadingMore,
        loadMore,
        revealSelectedKey,
    } = useInfrastructureNodeList({
        parentNodeId,
        includeTrash,
        selectedNode,
        normalizedValue,
    });

    const loading = listLoading || parentNodeLoading || selectedNodeLoading;

    const headerText: string = isRoot
        ? t("Companies")
        : (parentNodeData?.installedBaseNode?.title ?? "");

    const listRef = useRef<HTMLDivElement | null>(null);
    // Selection on the first page but below the fold: scroll to it once per
    // level/search/selection — again after every level/search change, since
    // the reset below scrolls it away. The rAF runs after that reset and layout.
    const revealedKeyRef = useRef<string | null>(null);
    useEffect(() => {
        listRef.current?.scrollTo({ top: 0 });
        revealedKeyRef.current = null;
    }, [parentNodeId, searchQuery]);

    useEffect(() => {
        if (!revealSelectedKey || revealedKeyRef.current === revealSelectedKey) return;
        revealedKeyRef.current = revealSelectedKey;
        requestAnimationFrame(() => {
            const scroller = listRef.current;
            const row = scroller?.querySelector<HTMLElement>('[data-selected="true"]');
            if (scroller && row) revealRowInScroller(scroller, row);
        });
    }, [revealSelectedKey]);

    const handleScroll = () => {
        const el = listRef.current;
        if (!el) return;
        if (el.scrollTop + el.clientHeight < el.scrollHeight - LIST_SCROLL_THRESHOLD) return;
        loadMore();
    };

    const handleSelectionToggle = (entryId: string) => {
        if (normalizedValue === entryId) {
            onChange?.(null);
        } else {
            onChange?.(entryId);
        }
    };

    const handleDrillInto = (entryId: string) => {
        setParentNodeHistory((prev) => [...prev, { id: parentNodeId }]);
        setParentNodeId(entryId);
    };

    const handleBack = () => {
        if (parentNodeHistory.length > 0) {
            const prev = parentNodeHistory[parentNodeHistory.length - 1];
            setParentNodeHistory((p) => p.slice(0, -1));
            setParentNodeId(prev.id);
        } else {
            setParentNodeId(scopedRootId);
        }
    };

    // Root nodes may come back with an empty/parents-only path — fall back to the title.
    const selectedPath = (selectedNode?.pathNodes?.map((n: any) => n.title).filter(Boolean).join(" / ") ?? "")
        || (selectedNode?.title ?? "");

    return (
        <Box sx={{ width: "100%" }}>
            {label && (
                <Typography variant="body2" sx={{ mb: 1, color: error ? "error.main" : "text.secondary" }}>
                    {label}
                </Typography>
            )}

            <Box
                sx={{
                    border: "1px solid",
                    borderColor: error ? "error.main" : "#ccc",
                    borderRadius: "5px",
                    overflow: "hidden",
                    backgroundColor: "background.paper",
                }}
            >
                {/* Header: current location + back */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        backgroundColor: "#fafafa",
                        borderBottom: "1px solid #eee",
                    }}
                >
                    {/* Fixed-width leading slot (back arrow, root radio or empty) so the
                        title x-position is identical across all levels/states. */}
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        {rootSelectable && scopedRootId !== null && atTopLevel ? (
                            <Radio
                                checked={normalizedValue === scopedRootId}
                                size="small"
                                onClick={() => handleSelectionToggle(scopedRootId)}
                                sx={{ p: 0.5 }}
                            />
                        ) : (
                            <IconButton
                                size="sm"
                                onClick={handleBack}
                                aria-label={t("Back") as string}
                                tabIndex={atTopLevel ? -1 : 0}
                                sx={atTopLevel ? { visibility: "hidden", pointerEvents: "none" } : undefined}
                            >
                                <ChevronLeftRounded fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            fontWeight: 700,
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {headerText}
                    </Typography>
                </Box>

                {/* Search */}
                <Box sx={{ px: 1.5, py: 1, borderBottom: "1px solid #eee" }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={t("Search") as string}
                        value={searchInput}
                        onChange={(e) => onSearchInputChange(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchRounded fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: searchInput !== "" ? (
                                <IconButton size="sm" onClick={onClearSearch}>
                                    <CloseRounded fontSize="small" />
                                </IconButton>
                            ) : null,
                        }}
                    />
                </Box>

                {/* List */}
                {/* position: relative → the rows' offsetParent (revealRowInScroller). */}
                <Box ref={listRef} onScroll={handleScroll} sx={{ height: LIST_HEIGHT, overflowY: "auto", position: "relative" }}>
                    {loading && filteredEntries.length === 0 ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : filteredEntries.length === 0 ? (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                            <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                {t("No entries")}
                            </Typography>
                        </Box>
                    ) : (
                        <>
                        {filteredEntries.map((entry, index) => {
                            const entryId = String(entry.id);
                            const isSelected = normalizedValue === entryId;
                            const isLast = index === filteredEntries.length - 1 && !loadingMore;
                            const childrenCount = entry.childrenNodeCount ?? 0;
                            const assetCount = entry.assetCount ?? 0;
                            return (
                                <Box
                                    key={entryId}
                                    data-selected={isSelected ? "true" : undefined}
                                    onClick={() => (selectRootOnly ? handleSelectionToggle(entryId) : handleDrillInto(entryId))}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        px: 1.5,
                                        py: 1,
                                        cursor: "pointer",
                                        borderBottom: isLast ? "none" : "1px solid #eee",
                                        backgroundColor: isSelected ? SELECTED_BG : "transparent",
                                        "&:hover": {
                                            backgroundColor: isSelected ? SELECTED_BG : "rgba(0, 0, 0, 0.04)",
                                        },
                                    }}
                                >
                                    <Radio
                                        checked={isSelected}
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSelectionToggle(entryId);
                                        }}
                                        sx={{ p: 0.5 }}
                                    />
                                    <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                                        {getInstalledBaseTypeIcon(entry.nodeType?.key ?? undefined, 20)}
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 500,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {entry.title}
                                        </Typography>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
                                                <Graph2Rounded sx={{ fontSize: 16, color: "text.secondary" }} />
                                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                    {childrenCount}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
                                                <BoxIcon size={16} />
                                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                    {assetCount}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    {!selectRootOnly && (
                                        <IconButton
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDrillInto(entryId);
                                            }}
                                            aria-label={t("Open") as string}
                                        >
                                            <ChevronRightRounded fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                            );
                        })}
                        {loadingMore && (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
                                <CircularProgress size={20} />
                            </Box>
                        )}
                        </>
                    )}
                </Box>

                {/* Selected indicator (always visible) */}
                <Box
                    sx={{
                        px: 1.5,
                        py: 1,
                        borderTop: "1px solid #eee",
                        backgroundColor: "#fafafa",
                    }}
                >
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {t("Selected")}:
                    </Typography>{" "}
                    {selectedNode ? (
                        <Typography
                            variant="caption"
                            sx={{ color: "text.primary", fontWeight: 500 }}
                            title={selectedPath}
                        >
                            {selectedPath}
                        </Typography>
                    ) : (
                        <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", fontStyle: "italic" }}
                        >
                            {t("No node selected.")}
                        </Typography>
                    )}
                </Box>
            </Box>

            {helperText && (
                <FormHelperText error={error} sx={{ ml: 1.5 }}>
                    {helperText}
                </FormHelperText>
            )}
        </Box>
    );
};

export default InfrastructureBrowser;
